'use client'

import { useViewportSize } from '@mantine/hooks'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { Button } from '@qr-front/ui/components/Button'
import { Separator } from '@qr-front/ui/components/Separator'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { isAxiosError } from 'axios'
import { ROUTES } from 'constants/routesConstants'
import { InputFieldEmailForm } from 'features/auth/components/InputFieldEmailForm'
import {
	APPLE_AUTH_TYPE,
	AppleAuthButton,
} from 'features/auth/components/social-auth/AppleAuthButton'
import { FacebookAuthButton } from 'features/auth/components/social-auth/FacebookAuthButton'
import { GoogleAuthButton } from 'features/auth/components/social-auth/GoogleAuthButton'
import { setAuthTokenCookie } from 'features/auth/utils/authCookies'
import {
	DEFAULT_HIDE_SOCIALS,
	hasVisibleSocialAuth,
} from 'features/auth/utils/hideSocials'
import { subscriptionApi } from 'features/purchase/subscription'
import { useSetAtom } from 'jotai'
import { RichTranslationValues, useTranslations } from 'next-intl'
import { ReactNode, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { User } from 'types/auth'

import { getApiClient } from '@/api'
import {
	dismissAllNotifications,
	showNotification,
} from '@/components/Notification'
import { API_ROUTES, DEFAULT_LOCALE, SEARCH_PARAMS } from '@/constants'
import { userAtom } from '@/global-state'
import { Link, useRouter } from '@/i18n-routing'
import { TTranslationFunction } from '@/types'
import { env, THideSocials } from '@/utils'
import { getValidErrorMessage } from '@/utils/apiUtils'
import { setCookieClient } from '@/utils/client'

const INVALID_CAPTCHA_MESSAGE_KEY =
	'public__cancel_subscription__invalid_captcha_message'

const CANCEL_REQUEST_ERROR_DISPLAY_KEYS: Record<string, string> = {
	'api.errors.cancelSubscription.noActiveSubscription':
		'public__cancel_subscription__no_active_subscription_message',
	'api.errors.cancelSubscription.rateLimited':
		'public__cancel_subscription__rate_limited_message',
	'api.errors.cancelSubscription.invalidCaptcha': INVALID_CAPTCHA_MESSAGE_KEY,
}

class CaptchaError extends Error {}

const getCancelRequestErrorMessage = (
	error: unknown,
	t: TTranslationFunction,
): string => {
	if (error instanceof CaptchaError) {
		return t(INVALID_CAPTCHA_MESSAGE_KEY)
	}

	if (isAxiosError(error)) {
		const rawKey =
			(error.response?.data as { message?: string | string[] })
				?.message ??
			(error.response?.data as { errorMessage?: string | string[] })
				?.errorMessage
		const errorKey = Array.isArray(rawKey) ? rawKey[0] : rawKey
		const displayKey = errorKey
			? CANCEL_REQUEST_ERROR_DISPLAY_KEYS[errorKey]
			: undefined

		if (displayKey) {
			return t(displayKey)
		}
	}

	return getValidErrorMessage(error, t)
}

export const CancelSubscriptionCard = ({
	hideSocials = DEFAULT_HIDE_SOCIALS,
	onCancelRequestCreated,
	source,
}: {
	hideSocials?: THideSocials
	onCancelRequestCreated: (cancelSubscriptionRequestId: string) => void
	source?: string
}) => {
	const t = useTranslations()
	const router = useRouter()
	const setUser = useSetAtom(userAtom)
	const { width } = useViewportSize()
	const isMobile = width < 740

	const turnstileRef = useRef<TurnstileInstance>(null)
	const turnstileErrorRef = useRef<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isSocialLoading, setIsSocialLoading] = useState(false)

	const emailPlaceholder = isMobile
		? t('public__cancel_subscription__form__email_placeholder__text_mobile')
		: t('public__cancel_subscription__form__email_placeholder__text')

	const form = useForm({
		defaultValues: { email: '' },
	})

	const authorizeUser = async (
		user: User,
		accessToken: string,
	): Promise<void> => {
		setUser(user)
		setAuthTokenCookie(accessToken, user.role)
		const userLanguage = user.language || DEFAULT_LOCALE
		setCookieClient('lang', userLanguage)

		if (user.use2fa) {
			router.push(
				`${ROUTES.auth.login}?${SEARCH_PARAMS.auth.key}=${SEARCH_PARAMS.auth.login2fa}&${SEARCH_PARAMS.auth.redirectUrl}=${ROUTES.cabinet.billing}`,
			)
			return
		}

		router.replace(ROUTES.cabinet.billing, { locale: userLanguage })
	}

	const onSocialSubmit = async (payload: {
		token: string
		type: string
		signup?: boolean
	}): Promise<void> => {
		const { token, type, signup } = payload
		if (!token || isSocialLoading) return
		try {
			await dismissAllNotifications()
			if (type === 'google') {
				const {
					data: { accessToken, user },
				} = await getApiClient().post(API_ROUTES.auth.googleLogin, {
					token,
				})
				await authorizeUser(user, accessToken)
			} else if (type === 'facebook') {
				const {
					data: { accessToken, user },
				} = await getApiClient().post(API_ROUTES.auth.facebookLogin, {
					token,
				})
				await authorizeUser(user, accessToken)
			} else if (type === 'apple') {
				const {
					data: { accessToken, user },
				} = await getApiClient().post(
					signup
						? API_ROUTES.auth.appleSignUp
						: API_ROUTES.auth.appleLogin,
					{ code: token },
				)
				await authorizeUser(user, accessToken)
			}
		} catch (error) {
			showNotification(getValidErrorMessage(error, t), {
				type: 'error',
				toastId: `social-login-error-${Date.now()}`,
			})
			console.error(error)
		} finally {
			setIsSocialLoading(false)
		}
	}

	const responseGoogle = (accessToken: string) => {
		if (!accessToken) return
		setIsSocialLoading(true)
		void onSocialSubmit({ token: accessToken, type: 'google' })
	}

	const responseFacebook = ({ accessToken }: { accessToken: string }) => {
		if (!accessToken) return
		setIsSocialLoading(true)
		void onSocialSubmit({ token: accessToken, type: 'facebook' })
	}

	const handleFacebookEmailDeclined = () => {
		showNotification(
			t('public__api__errors__facebookEmailPermissionError'),
			{
				type: 'error',
				toastId: `social-login-error-${Date.now()}`,
			},
		)
	}

	const handleAppleSubmit = (token: string, signup?: boolean) => {
		if (!token) return
		setIsSocialLoading(true)
		void onSocialSubmit({ token, type: 'apple', signup })
	}

	const handleAppleError = () => {
		setIsSocialLoading(false)
		showNotification(t('public__generic__unexpected_error'), {
			type: 'error',
			toastId: `social-login-error-${Date.now()}`,
		})
	}

	const handleTurnstileError = (errorCode: string) => {
		console.error(`Turnstile error: ${errorCode}`)
		turnstileErrorRef.current = errorCode
	}

	const getCaptchaToken = async (): Promise<string | undefined> => {
		if (turnstileErrorRef.current) {
			throw new CaptchaError(turnstileErrorRef.current)
		}
		try {
			return await turnstileRef.current?.getResponsePromise()
		} catch (error) {
			throw new CaptchaError((error as Error).message)
		}
	}

	const onSubmit = async (values: { email: string }): Promise<void> => {
		if (isSubmitting || isSocialLoading) return
		setIsSubmitting(true)
		try {
			const captchaToken = env.TURNSTILE_SITE_KEY
				? await getCaptchaToken()
				: undefined

			const data = await subscriptionApi.cancelPublicSubscriptionRequest(
				values.email,
				captchaToken,
				source,
			)

			if (data?.cancelSubscriptionRequestId) {
				onCancelRequestCreated(data.cancelSubscriptionRequestId)
			}
		} catch (error) {
			form.setError('email', {
				message: getCancelRequestErrorMessage(error, t),
			})
		} finally {
			turnstileErrorRef.current = null
			turnstileRef.current?.reset()
			setIsSubmitting(false)
		}
	}

	return (
		<div className="mb-30 mt-4 flex w-[600px] flex-col gap-6 rounded-10 bg-white p-6 shadow-generator desktop:mb-[12.5rem] desktop:mt-30 desktop:gap-8 desktop:p-8">
			<div className="flex flex-col gap-2">
				<h1 className="type-heading-3">
					{t('public__cancel_subscription__title')}
				</h1>
				<h2 className="type-body-medium text-dark-grey">
					{t('public__cancel_subscription__description')}
				</h2>
			</div>
			<Separator collapse />
			<div>
				<p className="type-body-small text-dark-grey">
					{t('public__cancel_subscription__form_instructions')}
				</p>
			</div>
			<FormProvider {...form}>
				<form
					className="flex flex-col gap-4"
					noValidate
					onSubmit={form.handleSubmit(onSubmit)}
				>
					<InputFieldEmailForm placeholder={emailPlaceholder} />
					{env.TURNSTILE_SITE_KEY && (
						<Turnstile
							ref={turnstileRef}
							siteKey={env.TURNSTILE_SITE_KEY}
							options={{ appearance: 'interaction-only' }}
							onSuccess={() => (turnstileErrorRef.current = null)}
							onError={handleTurnstileError}
						/>
					)}
					<Button
						type="submit"
						visual="default"
						size="large"
						className="w-full"
						disabled={isSubmitting || isSocialLoading}
					>
						{t('public__cancel_subscription__form_button')}
						{isSubmitting && <Spinner color="white" />}
					</Button>
				</form>
			</FormProvider>
			{hasVisibleSocialAuth(hideSocials) && (
				<>
					<div className="relative w-full">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-border-grey-50"></div>
						</div>
						<div className="text-sm relative flex justify-center">
							<span className="bg-white px-4 text-grey">
								{t('auth__common__separator_title')}
							</span>
						</div>
					</div>
					<div className="flex flex-col items-center gap-4">
						{!hideSocials.GOOGLE && (
							<GoogleAuthButton
								onSubmit={responseGoogle}
								variant="text"
							/>
						)}
						{!hideSocials.FACEBOOK && (
							<FacebookAuthButton
								onSubmit={responseFacebook}
								onEmailDeclined={handleFacebookEmailDeclined}
								variant="text"
							/>
						)}
						{!hideSocials.APPLE && (
							<AppleAuthButton
								onSubmit={handleAppleSubmit}
								onError={handleAppleError}
								authType={APPLE_AUTH_TYPE.Login}
								variant="text"
							/>
						)}
					</div>
				</>
			)}
			<div className="flex flex-col gap-4">
				<p className="type-body-small text-dark-grey [&_a]:text-blue">
					{t.rich(
						'public__cancel_subscription__ additional_info__email',
						{
							link: (chunks: ReactNode) => (
								<Link href={ROUTES.contact}>{chunks}</Link>
							),
						} as RichTranslationValues,
					)}
				</p>
				<p className="type-body-small text-dark-grey">
					{t(
						'public__cancel_subscription__ additional_info__account',
					)}
				</p>
			</div>
		</div>
	)
}
